import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import toast from "react-hot-toast";
import CustomPhoneInput from "./CustomPhoneInput";
import { userService } from "../../services/api";

const OTP_LENGTH = 6;

const MobileNumberUpdateModal = ({
  isOpen,
  onClose,
  onSuccess,
  initialMobile = "",
  title = "Update mobile number",
}) => {
  const [step, setStep] = useState("phone");
  const [mobile, setMobile] = useState(initialMobile || "");
  const [otpDigits, setOtpDigits] = useState(() => Array(OTP_LENGTH).fill(""));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const inputRefs = useRef([]);
  const modalRoot = useMemo(() => document.body, []);

  useEffect(() => {
    if (isOpen) {
      setStep("phone");
      setMobile(initialMobile || "");
      setOtpDigits(Array(OTP_LENGTH).fill(""));
      setError("");
      setIsSubmitting(false);
    }
  }, [initialMobile, isOpen]);

  useEffect(() => {
    if (isOpen && step === "otp" && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [isOpen, step]);

  if (!isOpen) {
    return null;
  }

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    onClose?.();
  };

  const handleSendOtp = async () => {
    if (!mobile || mobile.trim().length < 5) {
      setError("Enter a valid mobile number.");
      return;
    }

    setError("");
    setIsSubmitting(true);
    const formattedMobile = `+${mobile}`;

    try {
      const response = await userService.sendMobileChangeOtp({ mobile: formattedMobile });
      if (!response?.success) {
        throw new Error(response?.message || "Failed to send OTP");
      }
      toast.success("OTP sent to your registered email.");
      setStep("otp");
      setOtpDigits(Array(OTP_LENGTH).fill(""));
    } catch (err) {
      toast.error(err.message || "Unable to send OTP.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    if (isSubmitting) return;
    await handleSendOtp();
  };

  const handleVerify = async () => {
    const otp = otpDigits.join("");
    if (otp.length !== OTP_LENGTH) {
      setError("Enter the 6-digit code sent to your email.");
      return;
    }

    setError("");
    setIsSubmitting(true);
    const formattedMobile = `+${mobile}`;

    try {
      const response = await userService.verifyMobileChangeOtp({
        mobile: formattedMobile,
        otp,
      });
      if (!response?.success) {
        throw new Error(response?.message || "Failed to verify OTP");
      }

      toast.success("Mobile number updated successfully.");
      onSuccess?.(mobile);
      onClose?.();
    } catch (err) {
      toast.error(err.message || "Verification failed.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) {
      return;
    }

    setOtpDigits((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });

    if (value && index < OTP_LENGTH - 1) {
      const nextInput = inputRefs.current[index + 1];
      nextInput?.focus();
    }
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otpDigits[index] && index > 0) {
      const prevInput = inputRefs.current[index - 1];
      prevInput?.focus();
    }
    if (event.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (event.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData("text");
    if (!pasted) return;

    const digits = pasted.replace(/\D/g, "").slice(0, OTP_LENGTH).split("");
    if (!digits.length) return;

    const padded = [...digits];
    while (padded.length < OTP_LENGTH) {
      padded.push("");
    }

    setOtpDigits(padded);

    const focusIndex = Math.min(digits.length, OTP_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-[#0B0B0B]/50 px-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[#0B0B0B]">{title}</h2>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md p-2 text-[#6B7280] transition hover:bg-[#F3F4F6]"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <p className="mt-2 text-sm text-[#4B5563]">
          {step === "phone"
            ? "Enter the new mobile number. We'll send an OTP to your registered email address."
            : "Enter the 6-digit OTP that we sent to your registered email address."}
        </p>

        {step === "phone" && (
          <div className="mt-6">
            <CustomPhoneInput
              label="New mobile number"
              value={mobile}
              onChange={setMobile}
              placeholder="Enter mobile number"
              country="fr"
              disabled={isSubmitting}
            />
            {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
            <button
              type="button"
              onClick={handleSendOtp}
              disabled={isSubmitting}
              className="mt-6 w-full rounded-lg bg-[#2283FF] px-4 py-2 text-sm font-medium text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? "Sending..." : "Send OTP"}
            </button>
          </div>
        )}

        {step === "otp" && (
          <div className="mt-6">
            <div className="flex justify-between gap-2">
              {otpDigits.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={digit}
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  data-otp-index={index}
                  onChange={(event) =>
                    handleOtpChange(index, event.target.value)
                  }
                  onKeyDown={(event) => handleOtpKeyDown(index, event)}
                  onPaste={index === 0 ? handleOtpPaste : undefined}
                  className="h-12 w-10 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] text-center text-lg font-semibold text-[#0B0B0B] focus:border-[#2283FF] focus:outline-none focus:ring-2 focus:ring-[#2283FF33]"
                  disabled={isSubmitting}
                />
              ))}
            </div>
            {error && <p className="mt-2 text-sm text-red-500">{error}</p>}

            <div className="mt-4 flex flex-col items-center gap-2 text-sm text-[#6B7280]">
              <span>Didn't get the OTP?</span>
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={isSubmitting}
                className="text-[#2283FF] transition hover:underline disabled:cursor-not-allowed disabled:opacity-60"
              >
                Resend OTP
              </button>
            </div>

            <button
              type="button"
              onClick={handleVerify}
              disabled={isSubmitting}
              className="mt-6 w-full rounded-lg bg-[#2283FF] px-4 py-2 text-sm font-medium text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? "Verifying..." : "Verify"}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, modalRoot);
};

export default MobileNumberUpdateModal;
